import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { IconButton, Tooltip, Menu, MenuItem } from '@material-ui/core'
import clsx from 'clsx'
import Icon from '../../../../components/atoms/Icon'
import FlexRow from '../../../../components/molecules/FlexRow'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'

const useStyles = makeStyles(() => ({
  navigationBar: {
    paddingInline: 'var(--padding-sm)'
  },
  navButton: {
    padding: 'var(--padding-xs)'
  },
  itemInfo: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--dai-color-text)',
    fontWeight: 'normal',
    cursor: 'pointer',
    padding: 'var(--padding-xs) var(--padding-sm)',
    borderRadius: 'var(--border-radius-sm)',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'var(---dai-color-hover)'
    }
  },
  menuItem: {
    fontSize: 'var(--font-size-sm)',
    '&.Mui-selected': {
      backgroundColor: 'var(--dai-color-highlight)',
      color: 'var(--dai-contrast-highlight)',
      fill: 'var(--dai-contrast-highlight)',
      fontWeight: 600
    }
  },
  menuPaper: {
    backgroundColor: 'var(--dai-color-surface2)',
    marginTop: '8px'
  }
}))

function ReadingListNavigation ({ linkedList, currentScheduleId }) {
  const classes = useStyles()
  const history = useHistory()
  const [menuAnchor, setMenuAnchor] = useState(null)

  const currentNode = useMemo(() => {
    if (!linkedList || !currentScheduleId) return null
    return linkedList.find(node => node.scheduleId === currentScheduleId)
  }, [linkedList, currentScheduleId])

  const handleNavigate = (scheduleId) => {
    if (scheduleId) {
      history.push(`/denali-ai/reading-list/item/${scheduleId}`)
    }
  }

  const handleOpenMenu = (event) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setMenuAnchor(null)
  }

  const handleMenuItemClick = (scheduleId) => {
    handleNavigate(scheduleId)
    handleCloseMenu()
  }

  if (!currentNode) return null

  return (
    <FlexRow className={`${classes.navigationBar} justify-between items-center font-serif`}>
      <Tooltip title={currentNode.prev ? `Previous: ${currentNode.prevName}` : ''} arrow>
        <span>
          <IconButton
            className={classes.navButton}
            onClick={() => handleNavigate(currentNode.prev)}
            disabled={!currentNode.prev}
            size='small'
          >
            <Icon name='chevronLeft' />
          </IconButton>
        </span>
      </Tooltip>

      <FadeIn
        key={currentNode.scheduleId}
        className={classes.itemInfo}
        onClick={handleOpenMenu}
      >
        {currentNode.name}
        <Icon name='chevronDown' style={{ marginLeft: '4px', fontSize: '16px' }} />
      </FadeIn>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        getContentAnchorEl={null}
        PaperProps={{
          className: classes.menuPaper
        }}
      >
        {linkedList?.map((node) => (
          <MenuItem
            key={node.scheduleId}
            className={clsx('font-serif', classes.menuItem)}
            selected={node.scheduleId === currentScheduleId}
            onClick={() => handleMenuItemClick(node.scheduleId)}
          >
            <Icon name='benchmarks' style={{ marginRight: '8px', fontSize: '18px' }} />
            {node.name}
          </MenuItem>
        ))}
      </Menu>

      <Tooltip title={currentNode.next ? `Next Up: ${currentNode.nextName}` : ''} arrow>
        <span>
          <IconButton
            className={classes.navButton}
            onClick={() => handleNavigate(currentNode.next)}
            disabled={!currentNode.next}
            size='small'
          >
            <Icon name='chevronRight' />
          </IconButton>
        </span>
      </Tooltip>
    </FlexRow>
  )
}

ReadingListNavigation.propTypes = {
  linkedList: PropTypes.arrayOf(PropTypes.shape({
    scheduleId: PropTypes.string.isRequired,
    name: PropTypes.string,
    prev: PropTypes.string,
    prevName: PropTypes.string,
    next: PropTypes.string,
    nextName: PropTypes.string
  })),
  currentScheduleId: PropTypes.string
}

export default ReadingListNavigation
